import { buildNativeAppUrl, isNativeApp, resolvePublicWebOrigin } from './appLinks';

export type AuthProvider = 'google' | 'apple';

export const AUTH_CALLBACK_PATH = '/auth/callback';
const DEFAULT_AUTH_NEXT_PATH = '/account';
const AUTH_NEXT_PATH_STORAGE_KEY = 'dice1k.auth.nextPath';

export function normalizeAuthNextPath(raw?: string | null): string {
  if (!raw) return DEFAULT_AUTH_NEXT_PATH;
  if (!raw.startsWith('/')) return DEFAULT_AUTH_NEXT_PATH;
  if (raw.startsWith('//')) return DEFAULT_AUTH_NEXT_PATH;
  return raw;
}

export function resolveAuthCallbackBaseUrl(currentOrigin?: string): string {
  const browserOrigin = currentOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  if (isNativeApp()) {
    return buildNativeAppUrl(AUTH_CALLBACK_PATH);
  }
  return browserOrigin || resolvePublicWebOrigin();
}

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function storeAuthNextPath(nextPath?: string | null): string {
  const safeNextPath = normalizeAuthNextPath(nextPath);
  const storage = getStorage();
  storage?.setItem(AUTH_NEXT_PATH_STORAGE_KEY, safeNextPath);
  return safeNextPath;
}

export function readStoredAuthNextPath(): string {
  const storage = getStorage();
  return normalizeAuthNextPath(storage?.getItem(AUTH_NEXT_PATH_STORAGE_KEY));
}

export function clearStoredAuthNextPath(): void {
  const storage = getStorage();
  storage?.removeItem(AUTH_NEXT_PATH_STORAGE_KEY);
}

export function hasAuthCallbackPayload(url: URL): boolean {
  if (url.searchParams.has('code')) return true;
  return readAuthCallbackError(url) !== null;
}

export function buildAuthCallbackUrl(nextPath: string = DEFAULT_AUTH_NEXT_PATH): string {
  const baseUrl = resolveAuthCallbackBaseUrl();
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
  storeAuthNextPath(nextPath);

  if (!normalizedBaseUrl) {
    return AUTH_CALLBACK_PATH;
  }

  if (isNativeApp()) {
    return normalizedBaseUrl;
  }

  return `${normalizedBaseUrl}${AUTH_CALLBACK_PATH}`;
}

export function readAuthCallbackError(url: URL): string | null {
  const queryError = url.searchParams.get('error_description') || url.searchParams.get('error');
  if (queryError) return queryError;

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  return hashParams.get('error_description') || hashParams.get('error');
}
